<template>
	<v-card
		variant="outlined"
		class="resource-card-shell"
	>
		<template v-if="bucketData">
			<div class="entity-card">
				<!-- Header: icon + name -->
				<div class="entity-card__header">
					<span
						class="data-source-icon data-source-icon--bucket data-source-icon--32"
						role="img"
						aria-label="S3 Bucket"
					/>
					<div class="sgnl-flex sgnl-align-center">
						<v-tooltip
							bottom
							:allow-overflow="true"
						>
							<template v-slot:activator="{ props }">
								<span
									class="sgnl-paragraph-m-medium sgnl-text-color-default"
									v-bind="props"
									>{{ bucketData.Name }}</span
								>
							</template>
							<div>
								Bucket Owner: {{ bucketData.Account }}<br />
								Bucket ARN: arn:aws:s3:::{{ bucketData.Name }}<br />
								Unique Source ID: {{ bucketData.BucketID }}
							</div>
						</v-tooltip>
					</div>
				</div>

				<div class="entity-card__body">
					<!-- Badges: permissions -->
					<div class="sgnl-flex sgnl-align-center sgnl-flex-wrap sgnl-gap-8">
						<span
							class="entity-card__badge sgnl-text-color-secondary"
							:class="{ 'entity-card__badge--warning': !bucketData.Read }"
						>
							<Checkmark16 v-if="bucketData.Read" />
							<Close16 v-else />
							Read
						</span>
						<span
							class="entity-card__badge sgnl-text-color-secondary"
							:class="{ 'entity-card__badge--warning': !bucketData.Write }"
						>
							<Checkmark16 v-if="bucketData.Write" />
							<Close16 v-else />
							Write
						</span>
					</div>

					<!-- Metadata -->
					<div class="sgnl-paragraph-s-regular entity-card__meta sgnl-text-color-secondary">
						<div
							v-if="!$vuetify.display.mobile"
							class="sgnl-flex sgnl-align-center sgnl-gap-4"
						>
							<Calendar16 class="sgnl-icon-color-secondary" />Created:
							{{ formatDate(bucketData.CreatedAt) }}
						</div>
						<div class="sgnl-flex sgnl-align-center sgnl-gap-4">
							<Time16 class="sgnl-icon-color-secondary" />Last Updated:
							{{ formatDate(bucketData.UpdatedAt) }}
						</div>
					</div>
				</div>

				<!-- Actions -->
				<div class="entity-card__actions">
					<v-btn
						variant="flat"
						color="primary"
						target="_blank"
						:href="`https://s3.console.aws.amazon.com/s3/buckets/${bucketData.Name}?tab=objects`"
					>
						<VolumeFileStorage16 />
						<span>View Files</span>
						<ArrowUpRight16 />
					</v-btn>
					<v-btn
						variant="outlined"
						@click="requestSourceEdit"
					>
						<Settings16 />
						<span>Edit Configuration</span>
					</v-btn>
					<v-spacer />
					<v-btn
						variant="outlined"
						color="error"
						@click="showDisconnectDialog = true"
					>
						<TrashCan16 />
						<v-tooltip activator="parent">Disconnect</v-tooltip>
					</v-btn>
				</div>
			</div>
		</template>
		<DataSourceCardSkeleton v-else>
			<template #pills>
				<SkeletonBlock
					width="70px"
					height="22px"
					radius="var(--sgnl-border-radius-6)"
				/>
				<SkeletonBlock
					width="70px"
					height="22px"
					radius="var(--sgnl-border-radius-6)"
				/>
			</template>
			<template #text>
				<div
					v-if="!$vuetify.display.mobile"
					class="sgnl-flex sgnl-align-center"
				>
					<SkeletonBlock width="55%" />
				</div>
				<div class="sgnl-flex sgnl-align-center">
					<SkeletonBlock width="45%" />
				</div>
			</template>
			<template #actions>
				<SkeletonBlock
					width="155px"
					btn
				/>
				<SkeletonBlock
					width="180px"
					btn
				/>
			</template>
		</DataSourceCardSkeleton>
		<DataSourceConfigDialog
			v-if="showEditDialog"
			v-model:showDialog="showEditDialog"
			:editSourceData="editSourceData"
			@bucket-config-updated="bucketConfigUpdatedHandler"
			@bucket-connected="bucketConnectionHandler"
		/>
		<DisconnectBucketDialog
			v-if="showDisconnectDialog"
			v-model:showDialog="showDisconnectDialog"
			:bucketData="bucketData"
			@bucket-disconnected="bucketDisconnectedHandler"
			@close-dialog="showDisconnectDialog = false"
		/>
	</v-card>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { sdk } from "@/js/sdk";
import DataSourceConfigDialog from "@/components/DataSources/DataSourceConfigDialog.vue";
import DisconnectBucketDialog from "@/components/DataSources/DisconnectBucketDialog.vue";
import DataSourceCardSkeleton from "@/components/DataSources/DataSourceCardSkeleton.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import * as dsUtil from "@/components/DataSources/utilities";
import { AWSS3Bucket } from "@/types/api/dataSources";
import Checkmark16 from "@carbon/icons-vue/es/checkmark/16";
import Close16 from "@carbon/icons-vue/es/close/16";
import ArrowUpRight16 from "@carbon/icons-vue/es/arrow--up-right/16";
import VolumeFileStorage16 from "@carbon/icons-vue/es/volume--file-storage/16";
import Settings16 from "@carbon/icons-vue/es/settings/16";
import Calendar16 from "@carbon/icons-vue/es/calendar/16";
import Time16 from "@carbon/icons-vue/es/time/16";
import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";

type EditSourceData = {
	sourceType: "Bucket";
	sourceID: string;
	accountID: string;
	bucketName: string;
	readPermission: boolean;
	writePermission: boolean;
};

type ComponentData = {
	bucketData: undefined | AWSS3Bucket;
	editSourceData: undefined | EditSourceData;
	showEditDialog: boolean;
	showDisconnectDialog: boolean;
};

export default defineComponent({
	name: "BucketCard",
	components: {
		DataSourceConfigDialog,
		DisconnectBucketDialog,
		DataSourceCardSkeleton,
		SkeletonBlock,
		Checkmark16,
		Close16,
		ArrowUpRight16,
		VolumeFileStorage16,
		Settings16,
		Calendar16,
		Time16,
		TrashCan16,
	},
	props: {
		bucketID: { type: String, default: "" },
		initialBucketData: { type: Object as PropType<AWSS3Bucket>, default: undefined },
	},
	emits: {
		"bucket-disconnected": () => true,
		"bucket-disconnection-failed": () => true,
		"bucket-config-updated": () => true,
	},
	data: (): ComponentData => ({
		bucketData: undefined,
		editSourceData: undefined,
		showEditDialog: false,
		showDisconnectDialog: false,
	}),
	methods: {
		formatDate(dtString) {
			return dsUtil.formatDateDayString(1000 * dtString);
		},
		getBucketInfo() {
			return sdk.buckets
				.getOne(this.bucketID)
				.then((response) => {
					this.bucketData = response as AWSS3Bucket;
				})
				.catch((error) => console.log(error));
		},
		bucketConfigUpdatedHandler() {
			this.getBucketInfo();
			this.$emit("bucket-config-updated");
		},
		bucketConnectionHandler() {
			this.getBucketInfo();
		},
		requestSourceEdit() {
			if (!this.bucketData) return;
			this.editSourceData = {
				sourceType: "Bucket",
				sourceID: this.bucketData.BucketID,
				accountID: this.bucketData.Account,
				bucketName: this.bucketData.Name,
				readPermission: this.bucketData.Read,
				writePermission: this.bucketData.Write,
			};
			this.showEditDialog = true;
		},
		bucketDisconnectedHandler() {
			this.$emit("bucket-disconnected");
		},
		// * setters
		async setBucketConfig(newBucketConfig) {
			sdk.buckets
				.update(this.bucketID, newBucketConfig)
				.then(() => {
					this.getBucketInfo();
				})
				.catch((error) => console.log(error));
		},
	},

	mounted() {
		if (this.initialBucketData) {
			this.bucketData = { ...this.initialBucketData };
		} else {
			this.getBucketInfo();
		}
	},
});
</script>
